import { Suspense, useMemo } from 'react';
import { ServiceApiModel } from '../../../../../../../lib/api/rulemanager/types';
import { P2GBox, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { QuoteApiModel } from '~/lib/api/smartsend/types';
import useProcessedServices from '~/apps/rulemanager/components/services/useProcessedServices';
import ServiceCard from './service-card';

const mapQuotesToService = (quotes: QuoteApiModel[]): ServiceApiModel[] =>
  quotes.map(
    (q) =>
      ({
        courierSlug: q.Service.CourierId,
        slug: q.Service.Id,
        name: q.Service.Name,
        features: [],
        description: q.Service.Name,
        courierName: q.Service.CourierName
      }) as ServiceApiModel
  );

interface DefaultServiceProps {
  onSave: (slug: string, courier: string) => void;
  selectedServiceId: string;
  selectedCourierId: string;
  includeProtection: boolean;
  quotes: QuoteApiModel[];
  showSelectedCourier: boolean;
  isXs: boolean;
}

export default function DefaultServiceFromQuote(props: DefaultServiceProps) {
  // Filter to the services we want to show: Collection or All
  const filteredServices: ServiceApiModel[] = useMemo(() => mapQuotesToService(props.quotes), [props.quotes]);

  // Apply courier logos and merge couriers (UPS/Access Point -> UPS, Evri Collection/Evri -> Evri)
  const { processedCouriers, processedServices } = useProcessedServices(filteredServices);

  const selectedCourier = useMemo(
    () => processedCouriers.find((c) => c.courierSlug === props.selectedCourierId),
    [processedCouriers, props.selectedCourierId]
  );

  // Yey, the user has found a courier and service
  if (selectedCourier) {
    return (
      <>
        {props.showSelectedCourier && (
          <P2GBox>
            <P2GBox display="flex" gap={2} alignItems="center">
              <img src={selectedCourier.courierLogo} alt={selectedCourier.courier} width="130" />
              <P2GText>
                <strong>{selectedCourier.courier}</strong>
                <br />
                <P2GLink
                  variant="body2"
                  onClick={() => {
                    props.onSave(null, null);
                  }}
                >
                  Change Courier
                </P2GLink>
              </P2GText>
            </P2GBox>
          </P2GBox>
        )}
        {processedServices.length > 0 ? (
          <P2GBox mt={props.showSelectedCourier ? 2 : 0} mb={props.showSelectedCourier ? 2 : 0}>
            <Suspense>
              <P2GBox display="flex" flexDirection="column" gap={1}>
                {processedServices
                  .filter((s) => s.courierSlug === props.selectedCourierId)
                  .map((s) => (
                    <ServiceCard
                      isXs={props.isXs}
                      key={s.slug}
                      quote={props.quotes.find((x) => x.Service.Id === s.slug)}
                      includeProtection={props.includeProtection}
                      selected={props.selectedServiceId === s.slug}
                      onClick={() => {
                        props.onSave(s.slug, s.courierSlug);
                      }}
                    />
                  ))}
              </P2GBox>
            </Suspense>
          </P2GBox>
        ) : (
          <P2GText>No Results</P2GText>
        )}
      </>
    );
  }

  return (
    <P2GGrid container spacing={2}>
      {processedCouriers.map((c) => (
        <P2GGrid item key={c.courierSlug}>
          <img
            onClick={() => {
              props.onSave(c.slugs[0], c.courierSlug);
            }}
            src={c.courierLogo}
            alt={c.courier}
            width="130"
          />
        </P2GGrid>
      ))}
    </P2GGrid>
  );
}
